import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@leafwell/components';

import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';

import { clientId } from 'lib/tracking/gtag';
import { useMedicalCardParams } from 'hooks/useMedicalCardParams';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useEventsTracker } from 'contexts/eventsTracker';

const MEMBERSHIP_ONLY_STATE_CODE = 'fl';

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

const SignupSingleButton: React.FC<{
  stateCode: string;
  promoCode?: string | null;
  city?: string;
  formSubmitText: string;
  className?: string;
  generateLead?: boolean;
  variant?: 'primary' | 'secondary';
  ctaLocation?: string;
  onClick?: () => void;
}> = ({
  stateCode,
  city,
  formSubmitText,
  className = 'w-full lg:w-auto',
  generateLead = true,
  variant = 'primary',
  ctaLocation,
  promoCode = null,
  onClick,
}) => {
  const baseUrl = process.env.NEXT_PUBLIC_PRODUCT_ENDPOINT;
  const medicalCardParams = useMedicalCardParams();
  const [discountCode, setDiscountCode] = useState<string>(promoCode ?? null);
  const { query } = useRouter();
  const posthog = usePostHog();
  const [sessionId, setSessionId] = useState<string>('');
  const [distinctId, setDistinctId] = useState<string>('');
  const [fpromTid, setFpromTid] = useState<string | null>(null);
  const [fpromRefId, setFpromRefId] = useState<string | null>(null);

  const { trackEvent } = useEventsTracker();
  const { translations } = useWordPressContext();

  const medicalCardParamsString = useMemo(() => {
    if (!medicalCardParams || typeof medicalCardParams !== 'object') return '';
    return Object.entries(medicalCardParams)
      .filter(([, value]) => value != null && value !== '')
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
      )
      .join('&');
  }, [medicalCardParams]);

  useEffect(() => {
    setFpromTid(getCookie('_fprom_tid'));
    setFpromRefId(getCookie('_fprom_ref'));
  }, []);

  useEffect(() => {
    setSessionId(posthog.get_session_id() ?? '');
    setDistinctId(posthog.get_distinct_id() ?? '');
  }, [posthog]);

  useEffect(() => {
    if (query.discount_code && query.discount_code != '') {
      setDiscountCode(query.discount_code.toString().toUpperCase());
    }
  }, [query]);

  const isMembershipState =
    stateCode?.toLowerCase() === MEMBERSHIP_ONLY_STATE_CODE;
  const targetBaseUrl = isMembershipState
    ? `${process.env.NEXT_PUBLIC_LOGIN_URL}/membership`
    : baseUrl;

  const href = useMemo(() => {
    return `${targetBaseUrl}?${medicalCardParamsString}&session_id=${sessionId}&distinct_id=${distinctId}&state_code=${stateCode?.toLowerCase()}${
      city ? `&city=${city.toLowerCase()}` : ''
    }${discountCode ? `&discount_code=${discountCode}` : ''}${
      fpromRefId ? `&fp_ref=${fpromRefId}` : ''
    }${fpromTid ? `&fp_tid=${fpromTid}` : ''}`;
  }, [
    targetBaseUrl,
    medicalCardParamsString,
    sessionId,
    distinctId,
    stateCode,
    discountCode,
    fpromRefId,
    fpromTid,
  ]);

  const referrerData = medicalCardParams?.utm_referrer
    ? { page_referrer: medicalCardParams.utm_referrer }
    : {};

  const discountCodeData = discountCode ? { discount_code: discountCode } : {};
  const cityData = city ? { city: city } : {};
  const ctaLocationData = ctaLocation ? { cta_location: ctaLocation } : {};

  return (
    <Button
      label={formSubmitText}
      href={href}
      rounded={true}
      variant={variant}
      filled={true}
      className={className}
      onClick={() => {
        if (fpromTid) {
          posthog.setPersonProperties({ _fprom_tid: fpromTid });
        }
        if (fpromRefId) {
          posthog.setPersonProperties({ _fprom_ref: fpromRefId });
        }

        if (generateLead) {
          // save conversion on Google Ads and Facebook Ads to leafwell.us
          if (
            process.env.NODE_ENV === 'production' &&
            window?.location?.hostname === 'leafwell.us'
          ) {
            // @ts-ignore: function is being initialized on .us pages (index)
            gtag_report_conversion();
            // @ts-ignore: function is being initialized on .us pages (index)
            fbq('track', 'Lead');
          }

          trackEvent({
            event: 'generate_lead',
            service: 'medical_card',
            location: translations[stateCode.toUpperCase()],
            client_id: clientId(),
            ...cityData,
            ...referrerData,
            ...discountCodeData,
            ...ctaLocationData,
          });
        }

        if (onClick) {
          onClick();
        }
      }}
    />
  );
};

export default SignupSingleButton;
